import $ from 'zepto';
import { slideUpAndRemove, appendAndSlideDown } from '../../utils/dom';
import { assert } from '../../utils';
import { registerRowItemInForm } from './form';
import * as utils from './utils';

const addTypes = {
  PREPEND: 'PREPEND',
  APPEND: 'APPEND',
};

const removeTypes = {
  ALL: 'ALL',
  FIRST: 'FIRST',
  LAST: 'LAST',
};

const replaceTypes = removeTypes;

const items = {};

const registerRowItem = ({ field }) => {
  assert(field, '`field` is required');

  const { id } = field;
  const $row = utils.getFieldRow(field);

  field.$field
    .on('change', () => {
      field.removeErrorHighlight({
        type: addTypes.ALL,
        isError: true,
      });
    });

  const createAction = (action) => (options = {}) => {
    const $footerMessage = utils.getFooterMessage($row);
    const onComplete = () => {
      utils.removeUselessFooterMessageContainer($footerMessage);
      if ($.isFunction(options.onComplete)) {
        options.onComplete();
      }
    };

    const insternalOptions = {
      $footerMessage,
      onComplete,
    };

    return action(insternalOptions, options);
  };

  const removeMessageFactory = (options) => {
    const { hasErrorHighlight, isError, $message } = options;
    const removeMessage = createAction(({ onComplete }) => {
      if (!hasErrorHighlight && isError) {
        field.removeErrorHighlight();
      }

      slideUpAndRemove({ $element: $message, onComplete });
    });
    return () => {
      removeMessage();
    };
  };

  const addFooterMessage = createAction(({ $footerMessage, onComplete }, options) => {
    assert(options && options.message, '`options.message` is required');

    const {
      type = addTypes.APPEND,
      isError = false,
    } = options;
    const $message = utils.getMessageFromOptions(options);
    const hasErrorHighlight = field.hasErrorHighlight();

    if (!hasErrorHighlight && isError) {
      field.addErrorHighlight();
    }

    if (type === addTypes.APPEND) {
      utils.appendFooterMessage($footerMessage, $message, onComplete);
    }

    if (type === addTypes.PREPEND) {
      utils.prependFooterMessage($footerMessage, $message, onComplete);
    }

    return removeMessageFactory({
      hasErrorHighlight,
      isError,
      $message,
    });
  });

  const removeFooterMessage = createAction(({ $footerMessage, onComplete }, options) => {
    const {
      type = removeTypes.FIRST,
      isError = false,
    } = options;
    const hasErrorHighlight = field.hasErrorHighlight();

    if (hasErrorHighlight && isError) {
      field.removeErrorHighlight();
    }

    let addType;
    let $removedMessages;

    if (type === removeTypes.ALL) {
      $removedMessages = utils.removeAllFooterMessages($footerMessage, onComplete);
      addType = addTypes.APPEND;
    }

    if (type === removeTypes.FIRST) {
      $removedMessages = utils.removeFirstFooterMessage($footerMessage, onComplete);
      addType = addTypes.PREPEND;
    }

    if (type === removeTypes.LAST) {
      $removedMessages = utils.removeLastFooterMessage($footerMessage, onComplete);
      addType = addTypes.APPEND;
    }

    return () => {
      addFooterMessage({ message: $removedMessages, type: addType, isError: hasErrorHighlight });
    };
  });

  const replaceFooterMessage = createAction(({ $footerMessage, onComplete }, options) => {
    assert(options && options.message, '`options.message` is required');

    const {
      type = replaceTypes.FIRST,
      isError = false,
    } = options;
    const $message = utils.getMessageFromOptions(options);
    const hasErrorHighlight = field.hasErrorHighlight();

    let $replacedMessage;

    if (!hasErrorHighlight && isError) {
      field.addErrorHighlight();
    }

    if (type === replaceTypes.ALL) {
      // NOTE: When replacing all the messages, the current footer messages container
      // is being slided up and removed to avoid multiple animations, therefore a new
      // container is needed for the new message.
      const $newFooterMessage = utils.createFooterMessage();
      $newFooterMessage.append($message);

      $replacedMessage = utils.removeAllFooterMessages($footerMessage).children();

      appendAndSlideDown({
        $parent: $row,
        $element: $newFooterMessage,
        onComplete,
      });
    }

    if (type === replaceTypes.FIRST) {
      $replacedMessage = utils.removeFirstFooterMessage($footerMessage);
      utils.prependFooterMessage($footerMessage, $message, onComplete);
    }

    if (type === replaceTypes.LAST) {
      $replacedMessage = utils.removeLastFooterMessage($footerMessage);
      utils.appendFooterMessage($footerMessage, $message, onComplete);
    }

    return () => {
      if (!hasErrorHighlight && isError) {
        field.removeErrorHighlight();
      }

      // TO-DO check if the field had the highlight before restore it
      replaceFooterMessage({ message: $replacedMessage, type });
    };
  });

  const item = {
    id,
    get value() {
      // NOTE: this function is going to change when
      // radio buttons and checkboxes have been implemented
      return field.value;
    },
    hasErrorHighlight: field.hasErrorHighlight.bind(field),
    $row,
    $element: field.$select || field.$control,
    field,
    addFooterMessage,
    removeFooterMessage,
    replaceFooterMessage,
  };

  registerRowItemInForm(item);
  items[id] = item;
};

export {
  addTypes,
  removeTypes,
  replaceTypes,
  items,
  registerRowItemInForm,
  registerRowItem,
};
